import * as React from 'react';
import { ChevronLeftIcon as ChevronLeft, ChevronRightIcon as ChevronRight } from '@radix-ui/react-icons';
import { DayPicker } from 'react-day-picker';
import type { DayPickerSingleProps } from 'react-day-picker';
import { cn } from 'utilities/utils';
import { buttonVariants } from './button';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from './select';
import { ScrollArea } from './scrollArea';

export type CalendarProps = DayPickerSingleProps & {
  fromYear?: number;
  toYear?: number;
};

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  fromYear = 1940,
  toYear = new Date().getFullYear(),
  ...props
}: CalendarProps) {
  const today = new Date();
  const [month, setMonth] = React.useState(today.getMonth());
  const [year, setYear] = React.useState(today.getFullYear());
  const [selected, setSelected] = React.useState<Date | undefined>(undefined);

  const years = React.useMemo(() => {
    const yearsArr = [];
    for (let y = fromYear; y <= toYear; y++) {
      yearsArr.push(y);
    }
    return yearsArr;
  }, [fromYear, toYear]);

  const handleMonthChange = (value: string) => {
    setMonth(Number(value));
  };

  const handleYearChange = (value: string) => {
    setYear(Number(value));
  };

  const handlePreviousMonth = () => {
    if (month === 0) {
      setMonth(11);
      setYear((prev) => prev - 1);
    } else {
      setMonth((prev) => prev - 1);
    }
  };

  const handleNextMonth = () => {
    if (month === 11) {
      setMonth(0);
      setYear((prev) => prev + 1);
    } else {
      setMonth((prev) => prev + 1);
    }
  };

  const isPreviousDisabled = year === fromYear && month === 0;
  const isNextDisabled = year === toYear && month === 11;

  const monthsLocalized = React.useMemo(() => {
    return Array.from({ length: 12 }, (_, i) => props.locale.localize.month(i, { width: 'wide' }));
  }, [props.locale]);

  return (
    <div className={cn('p-3', className)}>
      <div className="flex justify-between items-center">
        <button
          onClick={handlePreviousMonth}
          disabled={isPreviousDisabled}
          className={cn(
            buttonVariants({ variant: 'outline' }),
            'h-7 w-7 p-0 opacity-50 hover:opacity-100',
            isPreviousDisabled && 'opacity-30 cursor-not-allowed hover:opacity-30',
          )}
          type="button"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <div className="flex items-center gap-2">
          <Select value={month.toString()} onValueChange={handleMonthChange}>
            <SelectTrigger className="pr-1.5 focus:ring-0">
              <SelectValue>{monthsLocalized[month]}</SelectValue>
            </SelectTrigger>
            <SelectContent position="popper">
              <ScrollArea className="h-80">
                {monthsLocalized.map((label, i) => (
                  <SelectItem key={i} value={i.toString()}>
                    {label}
                  </SelectItem>
                ))}
              </ScrollArea>
            </SelectContent>
          </Select>
          <Select value={year.toString()} onValueChange={handleYearChange}>
            <SelectTrigger className="pr-1.5 focus:ring-0">
              <SelectValue>{year}</SelectValue>
            </SelectTrigger>
            <SelectContent position="popper">
              <ScrollArea className="h-80">
                {years.map((y) => (
                  <SelectItem key={y} value={y.toString()}>
                    {y}
                  </SelectItem>
                ))}
              </ScrollArea>
            </SelectContent>
          </Select>
        </div>
        <button
          onClick={handleNextMonth}
          disabled={isNextDisabled}
          className={cn(
            buttonVariants({ variant: 'outline' }),
            'h-7 w-7 p-0 opacity-50 hover:opacity-100',
            isNextDisabled && 'opacity-30 cursor-not-allowed hover:opacity-30',
          )}
          type="button"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
      <DayPicker
        showOutsideDays={showOutsideDays}
        month={new Date(year, month)}
        selected={selected}
        onSelect={setSelected}
        className={cn('w-full', classNames?.months)}
        classNames={{
          months: 'flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0',
          month: 'space-y-4',
          caption: 'hidden',
          caption_label: 'text-sm font-medium',
          caption_dropdowns: 'flex justify-center gap-1',
          nav: 'space-x-1 flex items-center',
          nav_button: cn(
            buttonVariants({ variant: 'outline' }),
            'h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100',
          ),
          nav_button_previous: 'absolute left-1',
          nav_button_next: 'absolute right-1',
          table: 'w-full border-collapse space-y-1',
          head_row: 'flex',
          head_cell: 'text-muted-foreground rounded-md w-9 font-normal text-[0.8rem]',
          row: 'flex w-full mt-2',
          cell: 'text-center text-sm p-0 relative [&:has([aria-selected])]:bg-accent first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20',
          day: cn(buttonVariants({ variant: 'ghost' }), 'h-9 w-9 p-0 font-normal aria-selected:opacity-100'),
          day_selected:
            'bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground',
          day_today: 'bg-accent text-accent-foreground',
          day_outside: 'text-muted-foreground opacity-50',
          day_disabled: 'text-muted-foreground opacity-50',
          day_range_middle: 'aria-selected:bg-accent aria-selected:text-accent-foreground',
          day_hidden: 'invisible',
          ...classNames,
        }}
        {...props}
      />
    </div>
  );
}
Calendar.displayName = 'Calendar';

export { Calendar };
